import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SeoSettingsDataService } from '../../services/seo-settings-data.service';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { Subscription } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';

@Component({
  selector: 'kt-seo-settings-edit',
  templateUrl: './seo-settings-edit.component.html',
  styleUrls: ['./seo-settings-edit.component.scss']
})
export class SeoSettingsEditComponent implements OnInit, OnDestroy {

  form: FormGroup;
  dropdown = {
    merchant: this.dropdownHttpService.merchantSites,
    status: this.dropdownHttpService.statuses,
    metaRobot: this.dropdownHttpService.metaRobot,
  };
  messages$ = this.seoSettingsDataService.messages$;
  buttonLoading = false;

  private subscriptions = new Subscription();

  get isEdit(): boolean {
    return this.data.mode === 'edit';
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      mode: 'create' | 'edit',
      regions: any,
      seo: {
        id: number,
        site_id: number,
        country_code: string,
        domain: string[],
        gtag: string,
        meta_robot: string,
        status: number,
        remarks: string
      }
    },
    public dialogRef: MatDialogRef<SeoSettingsEditComponent>,
    private dropdownHttpService: DropdownHttpService,
    private seoSettingsDataService: SeoSettingsDataService,
  ) { }

  ngOnInit() {
    this.formInit();
    this.bindCascadeMetaRobotValidator();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  private bindCascadeMetaRobotValidator() {
    if (!this.isEdit) return;
    const metaRobotControl = this.form.get('meta_robot')!;
    const sub = this.form.get('cascade_meta_robot')!.valueChanges.subscribe((checked: boolean) => {
      if (checked) {
        metaRobotControl.setValidators([Validators.required]);
        metaRobotControl.enable();
      } else {
        metaRobotControl.setValidators([]);
        metaRobotControl.setValue(null);
        metaRobotControl.disable();
      }
      metaRobotControl.updateValueAndValidity();
    });
    this.subscriptions.add(sub);
  }

  onCloseDialog(value = false) {
    this.dialogRef.close(value);
  }

  async onSave() {
    const data = {
      ...this.form.value,
      domain: (this.form.value.domain).trim().split('\n')
    };

    if (this.isEdit) {
      if (!this.form.value.meta_robot) {
        delete data.meta_robot;
        delete data.cascade_meta_robot;
      } else if (this.form.value.cascade_meta_robot) {
        const resp = await Swal.fire({
          title: 'Are you sure?',
          text: `This will overwrite the Meta Robot value of all routes under this domain to "${this.form.value.meta_robot}".`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#dd3333',
          confirmButtonText: 'Yes, update all',
          cancelButtonText: 'Cancel',
          reverseButtons: true,
        });

        if (!resp.isConfirmed) {
          return;
        }
      }
    }

    this.buttonLoading = true;
    // To set "Save" button to disable (To prevent call API in multiple times when double click)
    this.form.setErrors({ 'invalid': true });

    switch (this.data.mode) {
      case 'create':
        this.seoSettingsDataService.add(data).pipe(
          tap((res: any) => {
            this.buttonLoading = false;
            // To enable "Save" button after get response
            this.form.setErrors(null);
          }),
          catchError((error) => {
            this.buttonLoading = false;
            // To enable "Save" button after get response
            this.form.setErrors(null);
            throw error;
          })
        ).subscribe();
        break;
      case 'edit':
        this.seoSettingsDataService.update(this.data.seo.id, data).pipe(
          tap((res: any) => {
            this.buttonLoading = false;
            // To enable "Save" button after get response
            this.form.setErrors(null);
          }),
          catchError((error) => {
            this.buttonLoading = false;
            // To enable "Save" button after get response
            this.form.setErrors(null);
            throw error;
          })
        ).subscribe();
        break;
    }
  }

  private formInit() {
    const isEdit = this.isEdit;
    this.form = new FormGroup({
      merchant: new FormControl(isEdit ? this.data.seo.site_id : null, [Validators.required]),
      country_code: new FormControl(isEdit ? this.data.seo.country_code : null, [Validators.required]),
      domain: new FormControl(isEdit ? (this.data.seo.domain).join('\n') : null, [Validators.required]),
      gtag: new FormControl(isEdit ? this.data.seo.gtag : null),
      meta_robot: new FormControl({ value: isEdit ? null : this.dropdown.metaRobot[0], disabled: isEdit }, isEdit ? [] : [Validators.required]),
      cascade_meta_robot: new FormControl(false),
      status: new FormControl(isEdit ? this.data.seo.status : 1, [Validators.required]),
      remarks: new FormControl(isEdit ? this.data.seo.remarks : null),
    });
  }

}
